import { QueryClientProvider } from "@tanstack/react-query"
import Head from "next/head"
import { useRouter } from "next/router"
import NProgress from "nprogress"
import "nprogress/nprogress.css"
import { useEffect, useState } from "react"
import "../styles/tailwind.css"

import Modal from "@p-ban/common/src/components/lib/Modal"
import { isBrowser } from "@p-ban/common/src/utils/CommonUtils"

import createQueryClient from "../api/createQueryClient"
import { RequiredAuth } from "../components/logic/RequiredAuth/RequiredAuth"
import MaintenanceInfo from "../components/schedule/MaintenanceInfo"
import GlobalStyle from "../css/GlobalStyle"
import FacebookAdTracking from "../lib/FacebookAdTracking"
import { useGoogleAnalytics } from "../lib/GoogleAnalytics"
import GoogleTagManager from "../lib/GoogleTagManager"
import * as Sentry from "../lib/Sentry"
import TwitterAdTracking from "../lib/TwitterAdTracking"
import type { AppProps } from "../types/next"

if (process.env.NODE_ENV !== "production") {
  if (isBrowser) {
    const startWorker = require("../api/__mocks__/worker").default
    startWorker()
    // beforeunloadでキャンセルされた場合、ServiceWorkerが停止してしまうので再度起動させます。
    window.addEventListener("beforeunload", () => setTimeout(startWorker, 100))
  }
}

Sentry.init()

const useProgressBar = ({ disabled }: { disabled: boolean }) => {
  const router = useRouter()
  useEffect(() => {
    if (disabled) {
      return
    }

    NProgress.configure({ showSpinner: false })

    const start = (_: unknown, { shallow }: { shallow: boolean }) => {
      if (!shallow) {
        NProgress.start()
      }
    }
    const done = () => NProgress.done()

    router.events.on("routeChangeStart", start)
    router.events.on("routeChangeComplete", done)
    router.events.on("routeChangeError", done)

    return () => {
      router.events.off("routeChangeStart", start)
      router.events.off("routeChangeComplete", done)
      router.events.off("routeChangeError", done)
    }
  }, [disabled, router.events])
}

Modal.setAppElement("#__next")

const App = (appProps: AppProps) => {
  const [queryClient] = useState(createQueryClient)

  const {
    Component,
    pageProps: {
      title = "P板.com:プリント基板ネット通販",
      font,
      seo: {
        description = "プリント基板のネット通販なら、ピーバンドットコム！　試作小ロット対応で、格安。基板設計、基板製造、メタルマスク製造、部品実装サービス。製造イニシャル無料で、設計から実装までコスト大幅ダウン！　1枚から製造、量産もOK、全国翌日着出荷。Webサイトから即時見積回答。部品シンボル約500点含む無料回路図CAD、ユーザー登録で部品ライブラリ約7,000点が利用可能なパターン設計CADもダウンロード。",
        noIndex = false,
        canonical,
        ogp,
      } = {},
      isIframe = false,
      requiredAuth = false,
      ...pageProps
    },
  } = appProps

  const { basePath } = useRouter()
  useGoogleAnalytics()
  useProgressBar({ disabled: isIframe })

  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <>
      <Head>
        <title>{title}</title>

        {/* SEO関連のタグ */}
        <meta name="description" content={description} lang="ja" />
        {noIndex && <meta name="robots" content="noindex" />}
        {canonical && <link rel="canonical" href={canonical} />}
        {ogp && (
          <>
            <meta property="og:url" content={ogp.url} />
            <meta property="og:title" content={ogp.title} />
            <meta property="og:type" content="website" />
            <meta property="og:image" content={ogp.image} />
            <meta property="og:description" content={ogp.description} />
          </>
        )}

        {isIframe && <base target="_top" />}
      </Head>
      <GlobalStyle font={font} basePath={basePath} />
      <GoogleTagManager />
      {process.env.NODE_ENV === "production" && !isIframe && (
        <>
          <FacebookAdTracking />
          <TwitterAdTracking />
        </>
      )}
      <QueryClientProvider client={queryClient}>
        <RequiredAuth enabled={requiredAuth}>
          {getLayout(<Component {...pageProps} />)}
          {!isIframe && <MaintenanceInfo />}
        </RequiredAuth>
      </QueryClientProvider>
    </>
  )
}

export default App
